import { useState, useEffect } from 'react'
import { Button, Input } from '../common'
import { clientService } from '../../services'
import type { Client } from '../../types/database.types'

interface MemberSelectorProps {
  providerId: string
  existingMemberIds: string[]
  onAddMembers: (clientIds: string[]) => void
  onCancel: () => void
  isLoading?: boolean
}

export function MemberSelector({
  providerId,
  existingMemberIds,
  onAddMembers,
  onCancel,
  isLoading = false,
}: MemberSelectorProps) {
  const [clients, setClients] = useState<Client[]>([])
  const [isLoadingClients, setIsLoadingClients] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const loadClients = async () => {
      try {
        // Load both active and invited clients - invited clients are valid
        // clients who just haven't accessed their programs yet
        const [activeClients, invitedClients] = await Promise.all([
          clientService.getClients(providerId, { status: 'active' }),
          clientService.getClients(providerId, { status: 'invited' }),
        ])
        const allClients = [...activeClients, ...invitedClients]
        // Filter out already existing members
        const availableClients = allClients.filter(
          (c) => !existingMemberIds.includes(c.id)
        )
        setClients(availableClients)
      } catch (error) {
        console.error('Failed to load clients:', error)
      } finally {
        setIsLoadingClients(false)
      }
    }
    loadClients()
  }, [providerId, existingMemberIds])

  const filteredClients = clients.filter(
    (c) =>
      c.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const toggleSelection = (clientId: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId)
    } else {
      newSelected.add(clientId)
    }
    setSelectedIds(newSelected)
  }

  const selectAll = () => {
    setSelectedIds(new Set(filteredClients.map((c) => c.id)))
  }

  const deselectAll = () => {
    setSelectedIds(new Set())
  }

  const handleSubmit = () => {
    onAddMembers(Array.from(selectedIds))
  }

  if (isLoadingClients) {
    return (
      <div className="py-8 text-center text-gray-500">Loading clients...</div>
    )
  }

  if (clients.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-gray-500 mb-4">
          No available clients to add. All your clients are already in this group.
        </p>
        <Button variant="secondary" onClick={onCancel}>
          Close
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={selectAll}
          className="text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
        >
          Select All
        </button>
        <button
          type="button"
          onClick={deselectAll}
          className="text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap"
        >
          Clear
        </button>
      </div>

      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
        {filteredClients.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No clients match your search
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredClients.map((client) => (
              <li
                key={client.id}
                onClick={() => toggleSelection(client.id)}
                className={`p-3 cursor-pointer hover:bg-gray-50 flex items-center gap-3 ${
                  selectedIds.has(client.id) ? 'bg-blue-50' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(client.id)}
                  onChange={() => toggleSelection(client.id)}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {client.full_name}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{client.email}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex justify-between items-center pt-2">
        <span className="text-sm text-gray-500">
          {selectedIds.size} selected
        </span>
        <div className="flex gap-3">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || selectedIds.size === 0}
          >
            {isLoading ? 'Adding...' : `Add ${selectedIds.size} Member${selectedIds.size !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </div>
    </div>
  )
}
